import { useTranslation } from 'react-i18next';
import { useUpdateBettieProfile } from '@pocapan/shared/domains/bettie/ui/hooks/useUpdateBettieProfile';
import { useSwitchBettieProfilePicture } from '@pocapan/shared/domains/bettie/ui/hooks/useSwitchBettieProfilePicture';
import { useSuccessPhotoCardToast } from '../../template/edit-collected-photo-cards/hooks/useSuccessPhotoCardToast';
import { fetchImageUrlToBase64DataUrl } from '@pocapan/shared/utils/resizeImage';
import type { components } from '@pocapan/shared/types/schema';

type ChatRoomUserPicture = components['schemas']['ApiV1BettieChatRoomUserPicture'] & {
  nickname?: string;
};

interface UseApplyBettieProfilePictureProps {
  chatRoomId?: string;
  selectedProfilePicture: ChatRoomUserPicture | null;
  localOriginalDataUrlByPictureUrl: Record<string, string>;
  usedRecommendPictureUrls: string[];
  onClearLocalUploadMemory: () => void;
  onRemoveLocalBackupPictureByUrl: (pictureUrl: string) => void;
}

export const useApplyBettieProfilePicture = ({
  chatRoomId,
  selectedProfilePicture,
  localOriginalDataUrlByPictureUrl,
  usedRecommendPictureUrls,
  onClearLocalUploadMemory,
  onRemoveLocalBackupPictureByUrl
}: UseApplyBettieProfilePictureProps) => {
  const { t } = useTranslation();
  const { mutateAsync: updateBettieProfile, isLoading: isUpdateLoading } = useUpdateBettieProfile();
  const { mutateAsync: switchBettieProfilePicture, isLoading: isSwitchLoading } =
    useSwitchBettieProfilePicture(chatRoomId ?? '');
  const successPhotoCardToast = useSuccessPhotoCardToast();

  // 로컬 사진을 현재 프로필 사진으로 업데이트하는 함수
  const updateLocalDataUrl = async ({
    dataUrl,
    originalDataUrl
  }: {
    dataUrl: string;
    originalDataUrl: string;
  }) => {
    const requestBody = {
      chat_room: {
        data_url: dataUrl,
        original_data_url: originalDataUrl
      }
    };
    const response = await updateBettieProfile({
      body: requestBody,
      chatRoomId: chatRoomId ?? ''
    });
    if (!response.chatRoom?.chatRoomId) {
      throw new Error('Failed to update bettie profile');
    }
    // 성공 후 local 메모리 정리 (서버에서 내려오는 backup 리스트로 대체)
    onClearLocalUploadMemory();
  };

  // 백업 사진을 현재 프로필 사진으로 스위치하는 함수
  const switchProfilePicture = async ({ backupPictureUrl }: { backupPictureUrl: string }) => {
    const requestBody = {
      picture: {
        backup_picture_url: backupPictureUrl
      }
    };
    const response = await switchBettieProfilePicture(requestBody);
    if (!response) {
      throw new Error('Failed to switch bettie profile picture');
    }
    // 추천(또는 URL)로 추가된 임시 로컬 목록이 서버 목록과 중복되지 않도록 정리
    onRemoveLocalBackupPictureByUrl(backupPictureUrl);
  };

  // 추천 사진을 현재 프로필 사진으로 업데이트하는 함수
  const updateRecommendedProfilePicture = async ({ pictureUrl }: { pictureUrl: string }) => {
    // 추천으로 받은 프사는 base64(Data URL)로 변환 후 전송
    const dataUrl = await fetchImageUrlToBase64DataUrl(pictureUrl);
    const requestBody = {
      chat_room: {
        data_url: dataUrl
      }
    };
    const response = await updateBettieProfile({
      body: requestBody,
      chatRoomId: chatRoomId ?? ''
    });
    if (!response.chatRoom?.chatRoomId) {
      throw new Error('Failed to update bettie profile');
    }
    // 추천으로 임시 추가된 항목은 로컬 목록에서 제거(서버 상태로 대체)
    onRemoveLocalBackupPictureByUrl(pictureUrl);
  };

  const handleApplySelectedProfilePicture = async () => {
    if (!chatRoomId || !selectedProfilePicture) return;

    try {
      const localOriginalDataUrl =
        localOriginalDataUrlByPictureUrl[selectedProfilePicture.pictureUrl];

      if (localOriginalDataUrl) {
        await updateLocalDataUrl({
          dataUrl: selectedProfilePicture.pictureUrl,
          originalDataUrl: localOriginalDataUrl
        });
      } else if (usedRecommendPictureUrls.includes(selectedProfilePicture.pictureUrl)) {
        await updateRecommendedProfilePicture({ pictureUrl: selectedProfilePicture.pictureUrl });
      } else {
        await switchProfilePicture({ backupPictureUrl: selectedProfilePicture.pictureUrl });
      }
      successPhotoCardToast('bottom', t('BettieChangeProfile.profileChanged') as string);
    } catch (error) {
      console.log(error);
    }
  };

  const handleApplyLocalUploadAsCurrentProfile = async ({
    dataUrl,
    originalDataUrl
  }: {
    dataUrl: string;
    originalDataUrl: string;
  }) => {
    if (!chatRoomId) return;
    try {
      await updateLocalDataUrl({ dataUrl, originalDataUrl });
      successPhotoCardToast('bottom', t('BettieChangeProfile.profileChanged') as string);
    } catch (error) {
      console.log(error);
    }
  };

  return {
    handleApplySelectedProfilePicture,
    handleApplyLocalUploadAsCurrentProfile,
    isApplyLoading: isUpdateLoading || isSwitchLoading
  };
};
